// dashboard/frontend/src/components/Campaign/CampaignRoundTab.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    CircularProgress,
    Collapse,
    Tooltip,
    LinearProgress,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormGroup,
    FormControlLabel,
    Snackbar,
    ListItemText,
    OutlinedInput,
    Divider,
    Alert,
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Send as SendIcon,
    ContentCopy as CopyIcon,
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Email as EmailIcon,
    Instagram as InstagramIcon,
    Twitter as TwitterIcon,
    LinkedIn as LinkedInIcon,
    Add as AddIcon,
    Schedule as ScheduleIcon,
    Delete as DeleteIcon,
    CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import api from '../../api/axios';
import ChannelFactory from './channels/ChannelFactory';
import EmailDraftRow from './channels/email/EmailDraftRow';
import InstagramDraftRow from './channels/instagram/InstagramDraftRow';
import InstagramDraftDialog from './channels/instagram/InstagramDraftDialog';
import { 
    fetchGmailAccounts, 
    fetchAttachments, 
    handleViewInGmail as emailViewInGmail,
    filterEmailContacts 
} from './channels/email/emailUtils';

// Channel icon mapping
const getChannelIcon = (channel) => {
    const icons = {
        email: <EmailIcon />,
        instagram_dm: <InstagramIcon />,
        twitter_dm: <TwitterIcon />,
        linkedin_dm: <LinkedInIcon />,
    };
    return icons[channel] || <EmailIcon />;
};

// Round Configuration Component
const RoundConfiguration = ({ round, onSave, gmailAccounts = [] }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState(round);
    const [selectedGmailAccounts, setSelectedGmailAccounts] = useState(
        round.gmail_accounts || []
    );

    useEffect(() => {
        setFormData(round);
        setSelectedGmailAccounts(round.gmail_accounts || []);
    }, [round]);

    const handleSave = async () => {
        const updatedRound = {
            ...formData,
            gmail_accounts: selectedGmailAccounts
        };
        
        await onSave(updatedRound);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setFormData(round);
        setSelectedGmailAccounts(round.gmail_accounts || []);
        setIsEditing(false);
    };

    const handleGmailAccountChange = (event) => {
        const value = event.target.value;
        setSelectedGmailAccounts(typeof value === 'string' ? 
            value.split(',') : value);
    };

    const getFieldsForChannel = () => {
        const commonFields = ['name', 'additional_details'];
        
        switch (round.channel) {
            case 'email':
                return [...commonFields, 'subject', 'custom_signature'];
            case 'instagram_dm':
            case 'twitter_dm':
            case 'linkedin_dm':
                return commonFields;
            default:
                return commonFields;
        }
    };

    const fieldLabels = {
        name: 'Round Name',
        subject: 'Email Subject',
        additional_details: 'Additional Details',
        custom_signature: 'Custom Signature',
    };

    const fieldHelperTexts = {
        name: 'Name for this round',
        subject: 'Email Subject (Optional) Supports variables: {first_name}, {last_name}, {company_name}, {title}. Leave empty for default: "Introduction: {your profile company name} + {company_name}"',
        additional_details: 'Any additional round specific context. Leave empty to use campaign default instructions',
        custom_signature: 'Custom signature for this round. Leave empty to use campaign default',
    };

    const visibleFields = getFieldsForChannel();

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getChannelIcon(round.channel)}
                <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                    Round Configuration - {round.channel.replace('_', ' ').toUpperCase()}
                </Typography>
                {!isEditing ? (
                    <IconButton onClick={() => setIsEditing(true)}>
                        <EditIcon />
                    </IconButton>
                ) : (
                    <Box>
                        <IconButton onClick={handleSave} color="primary">
                            <SaveIcon />
                        </IconButton>
                        <IconButton onClick={handleCancel}>
                            <CancelIcon />
                        </IconButton>
                    </Box>
                )}
            </Box>

            <Grid container spacing={3}>
                {/* Gmail Account Selection for Email Rounds */}
                {round.channel === 'email' && (
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Gmail Accounts</InputLabel>
                            <Select
                                multiple
                                value={selectedGmailAccounts}
                                onChange={handleGmailAccountChange}
                                input={<OutlinedInput label="Gmail Accounts" />}
                                renderValue={(selected) => 
                                    selected.map(id => {
                                        const account = gmailAccounts.find(acc => acc.id === id);
                                        return account ? account.email : id;
                                    }).join(', ')
                                }
                                disabled={!isEditing}
                            >
                                {gmailAccounts.map((account) => (
                                    <MenuItem key={account.id} value={account.id}>
                                        <Checkbox checked={selectedGmailAccounts.indexOf(account.id) > -1} />
                                        <ListItemText primary={account.email} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}

                {/* Dynamic fields based on channel */}
                {visibleFields.map((fieldName) => (
                    <Grid item xs={12} key={fieldName}>
                        <TextField
                            fullWidth
                            label={fieldLabels[fieldName]}
                            name={fieldName}
                            value={formData[fieldName] || ''}
                            onChange={(e) => setFormData({ ...formData, [fieldName]: e.target.value })}
                            helperText={fieldHelperTexts[fieldName]}
                            disabled={!isEditing}
                            multiline={fieldName === 'additional_details' || fieldName === 'custom_signature'}
                            rows={fieldName === 'additional_details' ? 4 : (fieldName === 'custom_signature' ? 6 : 1)}
                        />
                    </Grid>
                ))}
            </Grid>
        </Paper>
    );
};

// NEW FOR ATTACHMENTS - Attachment Row Component
const AttachmentRow = ({ attachment, onDelete, onUpdateDescription }) => {
    const [editing, setEditing] = useState(false);
    const [description, setDescription] = useState(attachment.description || '');

    const handleSave = () => {
        onUpdateDescription(attachment.id, description);
        setEditing(false);
    };

    const handleCancel = () => {
        setDescription(attachment.description || '');
        setEditing(false);
    };

    return (
        <TableRow>
            <TableCell>{attachment.filename}</TableCell>
            <TableCell>{(attachment.size / 1024).toFixed(2)} KB</TableCell>
            <TableCell>{attachment.content_type}</TableCell>
            <TableCell>
                {editing ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Add description..."
                            fullWidth
                        />
                        <IconButton size="small" onClick={handleSave} color="primary">
                            <SaveIcon />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancel}>
                            <CancelIcon />
                        </IconButton>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="body2">
                            {attachment.description || 'No description'}
                        </Typography>
                        <IconButton size="small" onClick={() => setEditing(true)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}
            </TableCell>
            <TableCell>
                {new Date(attachment.uploaded_at).toLocaleDateString()}
            </TableCell>
            <TableCell>
                <IconButton
                    color="error"
                    size="small"
                    onClick={() => onDelete(attachment.id)}
                >
                    <DeleteIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

// Attachments Section Component
const AttachmentsSection = ({ campaignId, roundId, attachments, setAttachments, setSnackbar }) => {
    const [uploadingAttachment, setUploadingAttachment] = useState(false);

    const handleAttachmentUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        // Client-side validation
        const maxFileSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxFileSize) {
            setSnackbar({
                open: true,
                message: `File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds maximum allowed size of 5MB`,
                severity: 'error'
            });
            event.target.value = '';
            return;
        }

        // Check if we're at the attachment limit
        if (attachments.length >= 5) {
            setSnackbar({
                open: true,
                message: 'Maximum 5 attachments allowed per round',
                severity: 'error'
            });
            event.target.value = '';
            return;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        
        setUploadingAttachment(true);
        try {
            // CHANGED: Round-specific endpoint
            const response = await api.post(
                `/campaigns/${campaignId}/rounds/${roundId}/attachments/`, 
                formData, 
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setAttachments([...attachments, response.data]);
            setSnackbar({
                open: true,
                message: 'Attachment uploaded successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error uploading attachment:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to upload attachment',
                severity: 'error'
            });
        } finally {
            setUploadingAttachment(false);
            event.target.value = '';
        }
    };
    
    const handleDeleteAttachment = async (attachmentId) => {
        if (!window.confirm('Are you sure you want to delete this attachment?')) {
            return;
        }
        
        try {
            // CHANGED: Round-specific endpoint
            await api.delete(`/campaigns/${campaignId}/rounds/${roundId}/attachments/${attachmentId}/`);
            setAttachments(attachments.filter(a => a.id !== attachmentId));
            setSnackbar({
                open: true,
                message: 'Attachment deleted successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error deleting attachment:', error);
            setSnackbar({
                open: true,
                message: 'Failed to delete attachment',
                severity: 'error'
            });
        }
    };

    const handleUpdateDescription = async (attachmentId, newDescription) => {
        try {
            // CHANGED: Round-specific endpoint
            const response = await api.patch(
                `/campaigns/${campaignId}/rounds/${roundId}/attachments/${attachmentId}/`,
                { description: newDescription }
            );
            
            setAttachments(attachments.map(a => 
                a.id === attachmentId ? response.data : a
            ));
            
            setSnackbar({
                open: true,
                message: 'Description updated successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error updating description:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update description',
                severity: 'error'
            });
        }
    };

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h6">Email Attachments</Typography>
                    <Typography variant="caption" color="textSecondary">
                        {/* CHANGED: Updated description */}
                        These attachments will only be included in emails for this specific round
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    component="label"
                    startIcon={uploadingAttachment ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    disabled={uploadingAttachment || attachments.length >= 5}
                >
                    Upload Attachment
                    <input
                        type="file"
                        hidden
                        onChange={handleAttachmentUpload}
                        accept=".pdf,.ppt,.pptx,.jpg,.jpeg,.png,.gif"
                    />
                </Button>
            </Box>
            
            {attachments.length > 0 ? (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Filename</TableCell>
                                <TableCell>Size</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Uploaded</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {attachments.map((attachment) => (
                                <AttachmentRow 
                                    key={attachment.id} 
                                    attachment={attachment}
                                    onDelete={handleDeleteAttachment}
                                    onUpdateDescription={handleUpdateDescription}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Alert severity="info">
                    No attachments uploaded yet. You can upload up to 5 files (max 5MB each, 20MB total).
                    <br />
                    Supported types: PDF, PowerPoint, JPG, PNG, GIF
                </Alert>
            )}
        </Paper>
    );
};

// Contact Draft Row Component
const ContactDraftRow = ({ 
    contact, 
    draft, 
    round, 
    onRefreshDraft, 
    onMarkSent, 
    onCopyDraft, 
    onViewInGmail,
    onDeleteDraft 
}) => {
    const [expanded, setExpanded] = useState(false);

    const getContactChannelInfo = () => {
        switch (round.channel) {
            case 'email':
                return contact.email;
            case 'instagram_dm':
                return contact.instagram_url;
            case 'twitter_dm':
                return contact.twitter_url;
            case 'linkedin_dm':
                return contact.linkedin_url;
            default:
                return 'N/A';
        }
    };

    const channelInfo = getContactChannelInfo();
    const hasChannelInfo = channelInfo && channelInfo !== 'N/A';

    const getDraftStatus = () => {
        if (!draft) return { color: 'default', label: 'Not Generated', icon: null };
        if (draft.status === 'sent') return { color: 'success', label: 'Sent', icon: <CheckCircleIcon fontSize="small" /> };
        if (draft.status === 'error') return { color: 'error', label: 'Error', icon: <ErrorIcon fontSize="small" /> };
        if (draft.gmail_draft_id) return { color: 'primary', label: 'In Gmail', icon: <EmailIcon fontSize="small" /> };
        return { color: 'warning', label: 'Draft', icon: null };
    };

    const status = getDraftStatus();

    return (
        <>
            <TableRow>
                <TableCell>
                    {contact.first_name} {contact.last_name}
                    <Typography variant="caption" display="block" color="textSecondary">
                        {contact.company_name}
                    </Typography>
                </TableCell>
                <TableCell>
                    {hasChannelInfo ? channelInfo : (
                        <Typography variant="caption" color="error">
                            Missing {round.channel.replace('_', ' ')} info
                        </Typography>
                    )}
                </TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {status.icon}
                        <Chip 
                            label={status.label} 
                            color={status.color} 
                            size="small" 
                        />
                    </Box>
                </TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {draft && (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={() => setExpanded(!expanded)}
                                >
                                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                                {round.channel === 'email' && draft.gmail_draft_url && (
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => window.open(draft.gmail_draft_url, '_blank')}
                                    >
                                        <VisibilityIcon />
                                    </IconButton>
                                )}
                                {round.channel === 'email' && !draft.gmail_draft_id && onViewInGmail && (
                                    <IconButton
                                        size="small"
                                        onClick={() => onViewInGmail(contact.email)}
                                    >
                                        <SendIcon />
                                    </IconButton>
                                )}
                                {draft.status !== 'sent' && (
                                    <IconButton
                                        size="small"
                                        onClick={() => onRefreshDraft(contact.id)}
                                    >
                                        <RefreshIcon />
                                    </IconButton>
                                )}
                                {onDeleteDraft && (
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => onDeleteDraft(draft.id)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </>
                        )}
                        {!draft && hasChannelInfo && (
                            <Tooltip title="Generate draft">
                                <IconButton
                                    size="small"
                                    onClick={() => onRefreshDraft(contact.id)}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </TableCell>
            </TableRow>
            {draft && (
                <TableRow>
                    <TableCell colSpan={4} sx={{ p: 0 }}>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                {draft.subject && (
                                    <TextField
                                        fullWidth
                                        label="Subject"
                                        value={draft.subject}
                                        margin="dense"
                                        variant="outlined"
                                        InputProps={{ readOnly: true }}
                                        sx={{ mb: 1 }}
                                    />
                                )}
                                <TextField
                                    fullWidth
                                    label="Message Content"
                                    value={draft.message ? draft.message.replace(/<br\s*\/?>/gi, '\n') : ''}
                                    multiline
                                    rows={8}
                                    variant="outlined"
                                    InputProps={{ readOnly: true }}
                                />
                                {draft.error_message && (
                                    <Alert severity="error" sx={{ mt: 1 }}>
                                        {draft.error_message}
                                    </Alert>
                                )}
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

// Batch Generation Dialog
const BatchGenerationDialog = ({ open, onClose, onGenerate, eligibleCount, round }) => {
    const [batchSize, setBatchSize] = useState('');
    const [generating, setGenerating] = useState(false);

    const handleGenerate = async () => {
        setGenerating(true);
        await onGenerate(batchSize ? parseInt(batchSize) : null);
        setGenerating(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Generate {round.channel.replace('_', ' ').toUpperCase()} Drafts
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong>{eligibleCount}</strong> contacts match your filters and are eligible for generation.
                </Typography>
                <TextField
                    fullWidth
                    label="Batch Size (Optional)"
                    type="number"
                    value={batchSize}
                    onChange={(e) => setBatchSize(e.target.value)}
                    helperText={`Leave empty to generate for all ${eligibleCount} contacts`}
                    sx={{ mt: 2 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button 
                    onClick={handleGenerate} 
                    variant="contained" 
                    disabled={generating}
                    startIcon={generating ? <CircularProgress size={16} /> : <SendIcon />}
                >
                    {generating ? 'Generating...' : 'Generate Drafts'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Location filter helpers. Location is not a column on UserContact; the Faire
// importer writes it into business_context as "... | Location: City, ST, USA |
// ..." where the country is Faire's ISO alpha-3 code (USA, SWE, GBR...), and
// sometimes the code alone. We read the last comma-separated part as the
// country and match it against a region list (alpha-3, alpha-2 and full names,
// so hand-entered contacts match too).
// ---------------------------------------------------------------------------
const REGION_COUNTRIES = {
    us: ['usa', 'us', 'united states', 'united states of america'],
    canada: ['can', 'ca', 'canada'],
    uk: ['gbr', 'gb', 'uk', 'united kingdom', 'great britain', 'england', 'scotland', 'wales', 'northern ireland'],
    eu: [
        'aut', 'at', 'austria', 'bel', 'be', 'belgium', 'bgr', 'bg', 'bulgaria', 'hrv', 'hr', 'croatia',
        'cyp', 'cy', 'cyprus', 'cze', 'cz', 'czechia', 'czech republic', 'dnk', 'dk', 'denmark',
        'est', 'ee', 'estonia', 'fin', 'fi', 'finland', 'fra', 'fr', 'france', 'deu', 'de', 'germany',
        'grc', 'gr', 'greece', 'hun', 'hu', 'hungary', 'irl', 'ie', 'ireland', 'ita', 'it', 'italy',
        'lva', 'lv', 'latvia', 'ltu', 'lt', 'lithuania', 'lux', 'lu', 'luxembourg', 'mlt', 'mt', 'malta',
        'nld', 'nl', 'netherlands', 'the netherlands', 'pol', 'pl', 'poland', 'prt', 'pt', 'portugal',
        'rou', 'ro', 'romania', 'svk', 'sk', 'slovakia', 'svn', 'si', 'slovenia', 'esp', 'es', 'spain',
        'swe', 'se', 'sweden',
    ],
    europe_other: ['che', 'ch', 'switzerland', 'nor', 'no', 'norway', 'isl', 'is', 'iceland', 'lie', 'li', 'liechtenstein'],
    anz: ['aus', 'au', 'australia', 'nzl', 'nz', 'new zealand'],
};
REGION_COUNTRIES.europe = [...REGION_COUNTRIES.eu, ...REGION_COUNTRIES.uk, ...REGION_COUNTRIES.europe_other];

const REGION_OPTIONS = [
    { value: 'any', label: 'Any location' },
    { value: 'us', label: 'United States' },
    { value: 'non_us', label: 'Outside the United States' },
    { value: 'eu', label: 'European Union' },
    { value: 'europe', label: 'Europe (EU + UK + CH/NO/IS)' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'canada', label: 'Canada' },
    { value: 'anz', label: 'Australia / New Zealand' },
];

// The text after "Location:" in business_context (or contact_snapshot), or ''.
const contactLocation = (contact) => {
    for (const field of [contact.business_context, contact.contact_snapshot]) {
        const part = (field || '').split('|').map(p => p.trim()).find(p => p.toLowerCase().startsWith('location:'));
        if (part) return part.slice('location:'.length).trim();
    }
    return '';
};

const contactCountry = (contact) => {
    const loc = contactLocation(contact);
    return loc ? loc.split(',').pop().trim().toLowerCase() : '';
};

// Contacts with no Location line never match a region other than 'any'.
const matchesRegion = (contact, region) => {
    if (!region || region === 'any') return true;
    const country = contactCountry(contact);
    if (!country) return false;
    if (region === 'non_us') return !REGION_COUNTRIES.us.includes(country);
    return (REGION_COUNTRIES[region] || []).includes(country);
};

// Main CampaignRoundTab component
const CampaignRoundTab = ({ campaign, round, contacts = [] }) => {
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [gmailAccounts, setGmailAccounts] = useState([]);
    const [batchDialogOpen, setBatchDialogOpen] = useState(false);
    const [generatingBatch, setGeneratingBatch] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(0);
    const [currentTask, setCurrentTask] = useState(null);
    const [progressMessage, setProgressMessage] = useState('');
    const [igDmDialogOpen, setIgDmDialogOpen] = useState(false);
    const [currentIgDraft, setCurrentIgDraft] = useState(null);

    // Add attachment state
    const [attachments, setAttachments] = useState([]);

    // IDs of contacts in this campaign already SENT an email
    // (used by the "exclude already emailed" draft filter)
    const [emailedContactIds, setEmailedContactIds] = useState(new Set());

    // IDs of contacts in this campaign already SENT an Instagram DM
    const [igContactedIds, setIgContactedIds] = useState(new Set());

    // Map of contactId -> ISO date of their most recent SENT email in this
    // campaign (used by the follow-up "last email >= N days ago" filter)
    const [lastEmailByContact, setLastEmailByContact] = useState({});

    useEffect(() => {
        fetchDrafts();
        fetchEmailedContacts();
        fetchIgContacted();
        if (round.channel === 'email') {
            fetchGmailAccounts(setGmailAccounts, setSnackbar);
            fetchAttachments(campaign.id, round.id, setAttachments, setSnackbar);
        }
    }, [round.id]);

    const [filters, setFilters] = useState({
        hasWooCommerce: false,
        hasShopify: false,
        hasDetectedIntegrations: false,
        customKeyword: '',
        excludeEmailed: false,
        excludeIgContacted: false,
        followUpDays: '',
        region: 'any',
        locationKeyword: '',
    });
    const [showFilters, setShowFilters] = useState(false);

    const fetchDrafts = async () => {
        setLoading(true);
        try {
            const response = await api.get('/contact-message-drafts/', {
                params: { 
                    campaign: campaign.id, 
                    round: round.id,
                    page_size: 10000,
                    no_pagination: 'true'
                }
            });
            const draftsData = response.data.results || response.data || [];
            setDrafts(Array.isArray(draftsData) ? draftsData : []);
        } catch (error) {
            console.error('Error fetching drafts:', error);
            setDrafts([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch the set of contacts in THIS campaign already sent an email
    // (any email round), so we can optionally exclude them from this round.
    const fetchEmailedContacts = async () => {
        try {
            const response = await api.get('/contact-message-drafts/', {
                params: {
                    campaign: campaign.id,
                    channel: 'email',
                    status: 'sent',
                    no_pagination: 'true'
                }
            });
            const sentDrafts = response.data.results || response.data || [];
            const ids = new Set(sentDrafts.map(d => d.contact));
            setEmailedContactIds(ids);

            // Track each contact's most recent sent-email date for follow-up timing
            const lastMap = {};
            sentDrafts.forEach(d => {
                if (!d.sent_at) return;
                if (!lastMap[d.contact] ||
                    new Date(d.sent_at).getTime() > new Date(lastMap[d.contact]).getTime()) {
                    lastMap[d.contact] = d.sent_at;
                }
            });
            setLastEmailByContact(lastMap);
        } catch (error) {
            console.error('Error fetching emailed contacts:', error);
            setEmailedContactIds(new Set());
        }
    };

    // Fetch contacts in THIS campaign already sent an Instagram DM, so we can
    // optionally exclude them (avoid double-touching across rounds).
    const fetchIgContacted = async () => {
        try {
            const response = await api.get('/contact-message-drafts/', {
                params: {
                    campaign: campaign.id,
                    channel: 'instagram_dm',
                    status: 'sent',
                    no_pagination: 'true'
                }
            });
            const sentDrafts = response.data.results || response.data || [];
            const ids = new Set(sentDrafts.map(d => d.contact));
            setIgContactedIds(ids);
        } catch (error) {
            console.error('Error fetching IG-contacted contacts:', error);
            setIgContactedIds(new Set());
        }
    };

    const handleSaveRoundConfig = async (updatedRound) => {
        try {
            await api.put(`/campaigns/${campaign.id}/rounds/${round.id}/`, updatedRound);
            setSnackbar({ 
                open: true, 
                message: 'Round configuration saved successfully', 
                severity: 'success' 
            });
        } catch (error) {
            setSnackbar({ 
                open: true, 
                message: 'Failed to save round configuration', 
                severity: 'error' 
            });
        }
    };

    const getEligibleContacts = () => {
        return contacts.filter(contact => {
            switch (round.channel) {
                case 'email':
                    return contact.email && contact.email.trim() !== '';
                case 'instagram_dm':
                    return contact.instagram_url && contact.instagram_url.trim() !== '';
                case 'twitter_dm':
                    return contact.twitter_url && contact.twitter_url.trim() !== '';
                case 'linkedin_dm':
                    return contact.linkedin_url && contact.linkedin_url.trim() !== '';
                default:
                    return false;
            }
        });
    };

    // Filter contacts based on selected criteria
    const getFilteredContacts = () => {
        let filtered = getEligibleContacts();
        
        // Apply WooCommerce filter
        if (filters.hasWooCommerce) {
            filtered = filtered.filter(contact => {
                const context = (contact.business_context || '').toLowerCase();
                return context.includes('woocommerce');
            });
        }
        
        // Apply Shopify filter
        if (filters.hasShopify) {
            filtered = filtered.filter(contact => {
                const context = (contact.business_context || '').toLowerCase();
                return context.includes('shopify');
            });
        }
        
        // Apply "has detected integrations" filter
        if (filters.hasDetectedIntegrations) {
            filtered = filtered.filter(contact => {
                const context = (contact.business_context || '').toLowerCase();
                return context.includes('detected integrations:');
            });
        }
        
        // Apply custom keyword filter
        if (filters.customKeyword && filters.customKeyword.trim() !== '') {
            const keyword = filters.customKeyword.toLowerCase().trim();
            filtered = filtered.filter(contact => {
                const context = (contact.business_context || '').toLowerCase();
                return context.includes(keyword);
            });
        }
        
        // Region filter (country parsed from the snapshot's Location: line)
        if (filters.region && filters.region !== 'any') {
            filtered = filtered.filter(contact => matchesRegion(contact, filters.region));
        }

        // Free-text location filter: city, state or country substring
        if (filters.locationKeyword && filters.locationKeyword.trim() !== '') {
            const needle = filters.locationKeyword.toLowerCase().trim();
            filtered = filtered.filter(contact => contactLocation(contact).toLowerCase().includes(needle));
        }

        // Exclude contacts already sent an email in this campaign
        if (filters.excludeEmailed) {
            filtered = filtered.filter(contact => !emailedContactIds.has(contact.id));
        }
        
        // Exclude contacts already sent an Instagram DM in this campaign
        if (filters.excludeIgContacted) {
            filtered = filtered.filter(contact => !igContactedIds.has(contact.id));
        }
        
        // Follow-up: only contacts whose most recent sent email was >= N days
        // ago. Requires a prior sent email, so never-emailed contacts are
        // excluded automatically.
        const followUpDays = parseInt(filters.followUpDays, 10);
        if (!isNaN(followUpDays) && followUpDays > 0) {
            const cutoff = Date.now() - followUpDays * 24 * 60 * 60 * 1000;
            filtered = filtered.filter(contact => {
                const lastSent = lastEmailByContact[contact.id];
                return lastSent && new Date(lastSent).getTime() <= cutoff;
            });
        }
        
        return filtered;
    };

    const getUnprocessedFilteredContacts = () => {
        const filteredContacts = getFilteredContacts();
        const draftsMap = {};
        drafts.forEach(draft => {
            draftsMap[draft.contact] = draft;
        });
        
        return filteredContacts.filter(contact => !draftsMap[contact.id]);
    };

    const getUnprocessedEligibleContacts = () => {
        const eligibleContacts = getEligibleContacts();
        const draftsMap = {};
        drafts.forEach(draft => {
            draftsMap[draft.contact] = draft;
        });
        
        return eligibleContacts.filter(contact => !draftsMap[contact.id]);
    };

    const handleBatchGenerate = async (batchSize = null) => {
        setGeneratingBatch(true);
        setGenerationProgress(0);
        setProgressMessage('Starting draft generation...');
        
        try {
            // Get filtered contact IDs
            const filteredContacts = getUnprocessedFilteredContacts();
            const contactIds = filteredContacts.map(c => c.id);
            
            if (contactIds.length === 0) {
                setSnackbar({
                    open: true,
                    message: 'No contacts match your filters',
                    severity: 'warning'
                });
                setGeneratingBatch(false);
                return;
            }
            
            // Limit by batch size if specified
            const contactsToGenerate = batchSize 
                ? contactIds.slice(0, batchSize)
                : contactIds;
            
            const response = await api.post(`/campaigns/${campaign.id}/generate_drafts_for_round/`, {
                round_id: round.id,
                contact_ids: contactsToGenerate, // Send specific contact IDs
                batch_size: batchSize
            });
            
            if (response.data.task_id) {
                setCurrentTask(response.data.task_id);
                pollTaskStatus(response.data.task_id);
            } else {
                setSnackbar({
                    open: true,
                    message: response.data.message || 'Batch generation completed',
                    severity: 'success'
                });
                await fetchDrafts();
            }
        } catch (error) {
            console.error('Error generating batch:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to generate drafts',
                severity: 'error'
            });
        } finally {
            setGeneratingBatch(false);
        }
    };

    const pollTaskStatus = async (taskId) => {
        const pollInterval = setInterval(async () => {
            try {
                const response = await api.get(`/campaigns/task-status/${taskId}/`);
                const taskData = response.data;
                
                if (taskData.state === 'SUCCESS' || taskData.state === 'FAILURE') {
                    clearInterval(pollInterval);  // STOP POLLING
                    setGeneratingBatch(false);
                    setCurrentTask(null);
                    
                    if (taskData.state === 'SUCCESS') {
                        setSnackbar({
                            open: true,
                            message: `Generated ${taskData.successful || 0} drafts successfully`,
                            severity: 'success'
                        });
                        await fetchDrafts();
                    } else {
                        setSnackbar({
                            open: true,
                            message: taskData.error || 'Batch generation failed',
                            severity: 'error'
                        });
                    }
                } else {
                    const progress = taskData.current && taskData.total 
                        ? (taskData.current / taskData.total) * 100 
                        : 0;
                    setGenerationProgress(progress);
                    setProgressMessage(taskData.status || 'Processing...');
                }
            } catch (error) {
                clearInterval(pollInterval);  // STOP ON ERROR
                setGeneratingBatch(false);
                setCurrentTask(null);
                console.error('Error polling task status:', error);
            }
        }, 2000);
    };

    const handleRefreshDraft = async (contactId) => {
        try {
            // Show loading state for this specific draft
            setDrafts(prevDrafts => 
                prevDrafts.map(d => 
                    d.contact === contactId 
                        ? { ...d, _regenerating: true }
                        : d
                )
            );

            // Call the backend to regenerate
            const response = await api.post(`/campaigns/${campaign.id}/generate_drafts_for_round/`, {
                round_id: round.id,
                contact_ids: [contactId],
                force_regenerate: true
            });

            // Poll for the task to complete
            if (response.data.task_id) {
                const pollForResult = setInterval(async () => {
                    try {
                        const taskResponse = await api.get(`/campaigns/task-status/${response.data.task_id}/`);
                        
                        if (taskResponse.data.state === 'SUCCESS') {
                            clearInterval(pollForResult);
                            
                            // Fetch all drafts for this round
                            const draftResponse = await api.get('/contact-message-drafts/', {
                                params: {
                                    campaign: campaign.id,
                                    round: round.id,
                                    page_size: 10000,
                                    no_pagination: 'true'
                                }
                            });
                            
                            const allDrafts = draftResponse.data.results || draftResponse.data || [];
                            const draftsArray = Array.isArray(allDrafts) ? allDrafts : [];
                            const newDraft = draftsArray.find(d => d.contact === contactId);
                            
                            if (newDraft) {
                                // Add transition flag to trigger smooth update
                                setDrafts(prevDrafts => {
                                    const index = prevDrafts.findIndex(d => d.contact === contactId);
                                    
                                    if (index >= 0) {
                                        const updated = [...prevDrafts];
                                        updated[index] = { ...newDraft, _justUpdated: true };
                                        return updated;
                                    } else {
                                        return [...prevDrafts, { ...newDraft, _justUpdated: true }];
                                    }
                                });
                                
                                // Remove transition flag after animation
                                setTimeout(() => {
                                    setDrafts(prevDrafts => 
                                        prevDrafts.map(d => {
                                            const { _justUpdated, ...rest } = d;
                                            return rest;
                                        })
                                    );
                                }, 500);
                                
                                setSnackbar({
                                    open: true,
                                    message: 'Draft regenerated successfully',
                                    severity: 'success'
                                });
                            } else {
                                // Fallback: refresh all drafts
                                setDrafts(draftsArray);
                                setSnackbar({
                                    open: true,
                                    message: 'Draft regenerated',
                                    severity: 'success'
                                });
                            }
                        } else if (taskResponse.data.state === 'FAILURE') {
                            clearInterval(pollForResult);
                            throw new Error(taskResponse.data.error || 'Regeneration failed');
                        }
                    } catch (pollError) {
                        clearInterval(pollForResult);
                        
                        // Remove loading state
                        setDrafts(prevDrafts => 
                            prevDrafts.map(d => {
                                const { _regenerating, ...rest } = d;
                                return d.contact === contactId ? rest : d;
                            })
                        );
                        
                        setSnackbar({
                            open: true,
                            message: 'Failed to check regeneration status',
                            severity: 'error'
                        });
                    }
                }, 1500);
                
                // Timeout after 30 seconds
                setTimeout(() => clearInterval(pollForResult), 30000);
            } else {
                // No task ID, just refresh
                await fetchDrafts();
                setSnackbar({
                    open: true,
                    message: 'Draft regenerated successfully',
                    severity: 'success'
                });
            }
        } catch (error) {
            console.error('Error regenerating draft:', error);
            
            // Remove loading state
            setDrafts(prevDrafts => 
                prevDrafts.map(d => {
                    const { _regenerating, ...rest } = d;
                    return d.contact === contactId ? rest : d;
                })
            );
            
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to regenerate draft',
                severity: 'error'
            });
        }
    };

    const handleMarkSent = async (draftId) => {
        try {
            await api.patch(`/contact-message-drafts/${draftId}/`, {
                status: 'sent'
            });
            await fetchDrafts();
            setSnackbar({ 
                open: true, 
                message: 'Draft marked as sent', 
                severity: 'success' 
            });
        } catch (error) {
            setSnackbar({ 
                open: true, 
                message: 'Failed to update draft status', 
                severity: 'error' 
            });
        }
    };

    const handleViewInGmail = (contactEmail) => {
        emailViewInGmail(campaign.id, round.id, contactEmail, setSnackbar, fetchDrafts);
    };

    const handleOpenIgDmDialog = (draft) => {
        // Find the full contact object from the contacts array
        const fullContact = contacts.find(c => c.id === draft.contact);
        
        // Attach the full contact to the draft
        const draftWithFullContact = {
            ...draft,
            contactData: fullContact || draft.contact
        };
        
        setCurrentIgDraft(draftWithFullContact);
        setIgDmDialogOpen(true);
    };

    const handleCloseIgDmDialog = () => {
        setIgDmDialogOpen(false);
        setCurrentIgDraft(null);
        fetchDrafts();
    };

    const handleIgDmSent = (draftId) => {
        setDrafts(prevDrafts => 
            prevDrafts.map(d => 
                d.id === draftId 
                    ? { ...d, status: 'sent', sent_at: new Date().toISOString() }
                    : d
            )
        );
    };

    const handleCopyDraft = () => {
        setSnackbar({ 
            open: true, 
            message: 'Draft copied to clipboard', 
            severity: 'success' 
        });
    };

    const handleDeleteDraft = async (draftId) => {
        if (!window.confirm(
            'Delete this message from ArgusCMS history? The contact becomes eligible again for future batches. This does NOT unsend any email already delivered.'
        )) {
            return;
        }
        try {
            await api.post(`/campaigns/${campaign.id}/delete_round_draft/`, {
                draft_id: draftId
            });
            
            // Immediately remove from local state
            setDrafts(drafts.filter(d => d.id !== draftId));
            
            setSnackbar({ 
                open: true, 
                message: 'Draft deleted successfully', 
                severity: 'success' 
            });
        } catch (error) {
            console.error('Error deleting draft:', error);
            setSnackbar({ 
                open: true, 
                message: 'Failed to delete draft', 
                severity: 'error' 
            });
        }
    };

    // Create drafts map for easy lookup
    const draftsMap = {};
    drafts.forEach(draft => {
        draftsMap[draft.contact] = draft;
    });

    // Calculate stats
    const draftStats = {
        eligible: getEligibleContacts().length,
        sent: drafts.filter(d => d.status === 'sent').length,
        drafts: drafts.filter(d => d.status !== 'sent').length,
        remaining: Math.max(0, getEligibleContacts().length - drafts.length)
    };

    const eligibleContacts = getEligibleContacts();
    const unprocessedEligibleContacts = getUnprocessedEligibleContacts();
    const [showSent, setShowSent] = useState(false);
    const [showAwaitingGeneration, setShowAwaitingGeneration] = useState(false);

    // Determine which contacts to show based on checkboxes
    const contactsToShow = React.useMemo(() => {
        const eligibleContacts = getEligibleContacts();
        const draftsMap = {};
        drafts.forEach(draft => {
            draftsMap[draft.contact] = draft;
        });
        
        let contactsWithDrafts = eligibleContacts.filter(contact => {
            const draft = draftsMap[contact.id];
            if (!draft) return false; // No draft yet
            
            // Filter based on "Show Sent" checkbox
            if (draft.status === 'sent' && !showSent) {
                return false; // Hide sent drafts unless checkbox is checked
            }
            
            return true;
        });
        
        // Add contacts awaiting generation if checkbox is checked
        if (showAwaitingGeneration) {
            const contactsAwaitingGeneration = eligibleContacts.filter(contact => {
                return !draftsMap[contact.id]; // No draft yet
            });
            
            contactsWithDrafts = [...contactsWithDrafts, ...contactsAwaitingGeneration];
        }
        
        return contactsWithDrafts;
    }, [drafts, showSent, showAwaitingGeneration, getEligibleContacts]);

    // Calculate counts for display
    const readyToSendCount = React.useMemo(() => {
        return drafts.filter(d => d.status !== 'sent').length;
    }, [drafts]);

    const sentCount = React.useMemo(() => {
        return drafts.filter(d => d.status === 'sent').length;
    }, [drafts]);

    const awaitingGenerationCount = React.useMemo(() => {
        const draftsMap = {};
        drafts.forEach(draft => {
            draftsMap[draft.contact] = draft;
        });
        return getEligibleContacts().filter(contact => !draftsMap[contact.id]).length;
    }, [drafts, getEligibleContacts]);

    return (
        <Box>
            {/* Round Configuration */}
            <RoundConfiguration 
                round={round} 
                onSave={handleSaveRoundConfig}
                gmailAccounts={gmailAccounts}
            />

            {/* NEW FOR ATTACHMENTS - Attachments Section (only for email rounds) */}
            {round.channel === 'email' && (
                <AttachmentsSection 
                    campaignId={campaign.id}
                    roundId={round.id}
                    attachments={attachments}
                    setAttachments={setAttachments}
                    setSnackbar={setSnackbar}
                />
            )}
            
            {/* Filter Section - Only show for Instagram/social channels */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">
                        Draft Filters (Optional)
                    </Typography>
                    <Button
                        size="small"
                        onClick={() => setShowFilters(!showFilters)}
                        startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                        {showFilters ? 'Hide' : 'Show'} Filters
                    </Button>
                </Box>
                
                <Collapse in={showFilters}>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Filter contacts based on their business context before generating drafts:
                        </Typography>
                        
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters.hasDetectedIntegrations}
                                        onChange={(e) => setFilters({ ...filters, hasDetectedIntegrations: e.target.checked })}
                                    />
                                }
                                label="Only contacts with detected integrations"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters.hasWooCommerce}
                                        onChange={(e) => setFilters({ ...filters, hasWooCommerce: e.target.checked })}
                                    />
                                }
                                label="Only contacts with WooCommerce"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters.hasShopify}
                                        onChange={(e) => setFilters({ ...filters, hasShopify: e.target.checked })}
                                    />
                                }
                                label="Only contacts with Shopify"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters.excludeEmailed}
                                        onChange={(e) => setFilters({ ...filters, excludeEmailed: e.target.checked })}
                                    />
                                }
                                label="Exclude contacts already emailed (this campaign)"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters.excludeIgContacted}
                                        onChange={(e) => setFilters({ ...filters, excludeIgContacted: e.target.checked })}
                                    />
                                }
                                label="Exclude contacts already sent an IG DM (this campaign)"
                            />
                        </FormGroup>
                        
                        <TextField
                            fullWidth
                            size="small"
                            label="Custom keyword in business context"
                            value={filters.customKeyword}
                            onChange={(e) => setFilters({ ...filters, customKeyword: e.target.value })}
                            placeholder="e.g., 'subscription', 'membership', 'B2B'"
                            sx={{ mt: 2 }}
                            helperText="Searches in business context field (case-insensitive)"
                        />

                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Follow-up: last email was at least N days ago"
                            value={filters.followUpDays}
                            onChange={(e) => setFilters({ ...filters, followUpDays: e.target.value })}
                            placeholder="7"
                            sx={{ mt: 2 }}
                            helperText="Only contacts whose last sent email was this many days ago or longer. Requires a prior sent email. Leave empty to disable."
                        />
                        
                        {/* Location filters (see contactLocation / matchesRegion above) */}
                        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ minWidth: 260 }}>
                                <InputLabel id="region-filter-label">Region</InputLabel>
                                <Select
                                    labelId="region-filter-label"
                                    label="Region"
                                    value={filters.region}
                                    onChange={(e) => setFilters({ ...filters, region: e.target.value })}
                                >
                                    {REGION_OPTIONS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                size="small"
                                label="Location contains"
                                placeholder="e.g. London, Ontario, France"
                                value={filters.locationKeyword}
                                onChange={(e) => setFilters({ ...filters, locationKeyword: e.target.value })}
                                helperText="Matches the Location line from the Faire import (city, state or country). Contacts without a location only match 'Any location'."
                                sx={{ minWidth: 320 }}
                            />
                        </Box>

                        {/* Show filtered count */}
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                <strong>{getFilteredContacts().length}</strong> contacts match your filters
                            </Typography>
                        </Alert>
                    </Box>
                </Collapse>
            </Paper>

            {/* Generation Controls */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                        Draft Generation
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setBatchDialogOpen(true)}
                        disabled={getUnprocessedFilteredContacts().length === 0}
                        startIcon={<SendIcon />}
                    >
                        Generate Drafts ({getUnprocessedFilteredContacts().length})
                    </Button>
                </Box>
                
                {/* Progress indicator */}
                {generatingBatch && (
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ flexGrow: 1, mr: 2 }}>
                                <LinearProgress variant="determinate" value={generationProgress} />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {Math.round(generationProgress)}%
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {progressMessage}
                        </Typography>
                    </Box>
                )}
                
                {/* Stats display */}
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary">Eligible</Typography>
                        <Typography variant="h6">{draftStats.eligible}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary">Sent</Typography>
                        <Typography variant="h6" color="success.main">{draftStats.sent}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary">Drafts</Typography>
                        <Typography variant="h6" color="warning.main">{draftStats.drafts}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary">Remaining</Typography>
                        <Typography variant="h6">{draftStats.remaining}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Drafts Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Contact</TableCell>
                            <TableCell>{round.channel.replace('_', ' ')}</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Actions
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={showSent}
                                                onChange={(e) => setShowSent(e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Typography variant="caption">
                                                Show Sent ({sentCount})
                                            </Typography>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={showAwaitingGeneration}
                                                onChange={(e) => setShowAwaitingGeneration(e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Typography variant="caption">
                                                Show Awaiting Generation ({awaitingGenerationCount})
                                            </Typography>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : contactsToShow.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    {round.channel === 'email' 
                                        ? 'No drafts generated yet. Click "Generate Drafts" to create email drafts.'
                                        : (() => {
                                            if (!showSent && !showAwaitingGeneration && readyToSendCount === 0) {
                                                if (sentCount > 0) {
                                                    return 'All drafts have been sent! ✓ Check "Show Sent" to review them.';
                                                } else if (awaitingGenerationCount > 0) {
                                                    return 'No drafts generated yet. Check "Show Awaiting Generation" or click "Generate Drafts".';
                                                }
                                            }
                                            return 'No contacts to display with current filters.';
                                        })()
                                    }
                                </TableCell>
                            </TableRow>
                        ) : (
                            contactsToShow.map((contact) => {
                                const draft = draftsMap[contact.id];
                                
                                // Email channel
                                if (round.channel === 'email') {
                                    return (
                                        <EmailDraftRow
                                            key={contact.id}
                                            contact={contact}
                                            draft={draft}
                                            onViewInGmail={handleViewInGmail}
                                            onRefreshDraft={handleRefreshDraft}
                                            onDeleteDraft={handleDeleteDraft}
                                        />
                                    );
                                }
                                
                                // Instagram channel
                                if (round.channel === 'instagram_dm') {
                                    return (
                                        <InstagramDraftRow
                                            key={contact.id}
                                            contact={contact}
                                            draft={draft}
                                            onOpenDialog={handleOpenIgDmDialog}
                                            onRefreshDraft={handleRefreshDraft}
                                            onDeleteDraft={handleDeleteDraft}
                                        />
                                    );
                                }
                                
                                // Fallback for other channels (keep old ContactDraftRow)
                                return (
                                    <ContactDraftRow
                                        key={contact.id}
                                        contact={contact}
                                        draft={draftsMap[contact.id]}
                                        round={round}
                                        onRefreshDraft={handleRefreshDraft}
                                        onMarkSent={handleMarkSent}
                                        onCopyDraft={handleCopyDraft}
                                        onViewInGmail={null}
                                        onDeleteDraft={handleDeleteDraft}
                                    />
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Batch Generation Dialog */}
            <BatchGenerationDialog
                open={batchDialogOpen}
                onClose={() => setBatchDialogOpen(false)}
                onGenerate={handleBatchGenerate}
                eligibleCount={unprocessedEligibleContacts.length}
                round={round}
            />

            {/* Instagram DM Dialog */}
            {round.channel === 'instagram_dm' && igDmDialogOpen && currentIgDraft && (
                <InstagramDraftDialog
                    open={igDmDialogOpen}
                    onClose={handleCloseIgDmDialog}
                    initialDraft={currentIgDraft}
                    campaignId={campaign.id}
                    roundId={round.id}
                    onDraftSent={handleIgDmSent}
                    onSnackbar={setSnackbar}
                />
            )}

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CampaignRoundTab;